/**
 * Safe Area Handling for Capacitor
 * Manages safe area insets for notched devices (iPhone X+, etc.)
 */

import { StatusBar, Style } from '@capacitor/status-bar';
import { isNativePlatform, isIOS } from './platform';

export interface SafeAreaInsets {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

// Default safe area insets (will be overridden by actual device values)
let currentInsets: SafeAreaInsets = {
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
};

/**
 * Initialize safe area handling
 */
export async function initializeSafeArea(): Promise<SafeAreaInsets> {
  if (!isNativePlatform()) {
    return currentInsets;
  }

  // Get safe area insets from CSS env() variables
  // These are automatically set by the system
  if (typeof window !== 'undefined') {
    const style = getComputedStyle(document.documentElement);

    currentInsets = {
      top: parseInt(style.getPropertyValue('--sat') || '0', 10) ||
           parseInt(getComputedStyle(document.documentElement).getPropertyValue('env(safe-area-inset-top)') || '0', 10),
      bottom: parseInt(style.getPropertyValue('--sab') || '0', 10) ||
              parseInt(getComputedStyle(document.documentElement).getPropertyValue('env(safe-area-inset-bottom)') || '0', 10),
      left: parseInt(style.getPropertyValue('--sal') || '0', 10) ||
            parseInt(getComputedStyle(document.documentElement).getPropertyValue('env(safe-area-inset-left)') || '0', 10),
      right: parseInt(style.getPropertyValue('--sar') || '0', 10) ||
             parseInt(getComputedStyle(document.documentElement).getPropertyValue('env(safe-area-inset-right)') || '0', 10),
    };

    // Set CSS custom properties for easy access
    document.documentElement.style.setProperty('--safe-area-top', `${currentInsets.top}px`);
    document.documentElement.style.setProperty('--safe-area-bottom', `${currentInsets.bottom}px`);
    document.documentElement.style.setProperty('--safe-area-left', `${currentInsets.left}px`);
    document.documentElement.style.setProperty('--safe-area-right', `${currentInsets.right}px`);
  }

  return currentInsets;
}

/**
 * Get current safe area insets
 */
export function getSafeAreaInsets(): SafeAreaInsets {
  return { ...currentInsets };
}

/**
 * Set status bar style
 */
export async function setStatusBarStyle(style: 'light' | 'dark'): Promise<void> {
  if (!isNativePlatform()) {
    return;
  }

  try {
    // Param is the TEXT color you want. Capacitor's Style enum is named for
    // the BACKGROUND (Style.Dark = dark bg = LIGHT text) — inverted from
    // intuition, which is exactly how we shipped white-on-white on Android.
    await StatusBar.setStyle({
      style: style === 'dark' ? Style.Light : Style.Dark,
    });
  } catch (error) {
    console.warn('Could not set status bar style:', error);
  }
}

/**
 * Set status bar background color (Android only)
 */
export async function setStatusBarColor(color: string): Promise<void> {
  if (!isNativePlatform() || isIOS()) {
    return;
  }

  try {
    await StatusBar.setBackgroundColor({ color });
  } catch (error) {
    console.warn('Could not set status bar color:', error);
  }
}

/**
 * Show status bar
 */
export async function showStatusBar(): Promise<void> {
  if (!isNativePlatform()) {
    return;
  }

  try {
    await StatusBar.show();
  } catch (error) {
    console.warn('Could not show status bar:', error);
  }
}

/**
 * Hide status bar
 */
export async function hideStatusBar(): Promise<void> {
  if (!isNativePlatform()) {
    return;
  }

  try {
    await StatusBar.hide();
  } catch (error) {
    console.warn('Could not hide status bar:', error);
  }
}

/**
 * Set status bar to overlay web view (iOS)
 */
export async function setStatusBarOverlay(overlay: boolean): Promise<void> {
  if (!isNativePlatform()) {
    return;
  }

  try {
    await StatusBar.setOverlaysWebView({ overlay });
  } catch (error) {
    console.warn('Could not set status bar overlay:', error);
  }
}

/**
 * Apply safe area padding to an element
 */
export function applySafeAreaPadding(
  element: HTMLElement,
  sides: ('top' | 'bottom' | 'left' | 'right')[] = ['top', 'bottom'],
): void {
  if (sides.includes('top')) {
    element.style.paddingTop = `max(${element.style.paddingTop || '0px'}, env(safe-area-inset-top, 0px))`;
  }
  if (sides.includes('bottom')) {
    element.style.paddingBottom = `max(${element.style.paddingBottom || '0px'}, env(safe-area-inset-bottom, 0px))`;
  }
  if (sides.includes('left')) {
    element.style.paddingLeft = `max(${element.style.paddingLeft || '0px'}, env(safe-area-inset-left, 0px))`;
  }
  if (sides.includes('right')) {
    element.style.paddingRight = `max(${element.style.paddingRight || '0px'}, env(safe-area-inset-right, 0px))`;
  }
}

/**
 * Get CSS for safe area padding
 */
export function getSafeAreaCSS(
  sides: ('top' | 'bottom' | 'left' | 'right')[] = ['top', 'bottom'],
): Record<string, string> {
  const css: Record<string, string> = {};

  if (sides.includes('top')) {
    css.paddingTop = 'env(safe-area-inset-top, 0px)';
  }
  if (sides.includes('bottom')) {
    css.paddingBottom = 'env(safe-area-inset-bottom, 0px)';
  }
  if (sides.includes('left')) {
    css.paddingLeft = 'env(safe-area-inset-left, 0px)';
  }
  if (sides.includes('right')) {
    css.paddingRight = 'env(safe-area-inset-right, 0px)';
  }

  return css;
}
